import { type FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button, ThemeProvider } from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';

import { AdminPage } from 'components/admin';

import NamesAndEmailsInputBlock, {
  type InputStateT,
} from 'components/admin/NamesAndEmailsInputBlock';

import { createArtist } from 'services/artists';

import { P_ADMIN_PRACTITIONERS, useGlobalState, useGlobalStateObject } from 'utils';

import IconInfo from 'assets/images/icon-info-20px.svg';

import throbberTheme from 'themes/AppThrobber/black.scss';
import secondaryButtonTheme from 'themes/Button/admin/secondary.scss';

import inputTheme from 'themes/Input/admin/variant-56px.scss';

import S from './style.scss';

const AdminAddNewPractitioner: FunctionComponent = () => {
  const [busy, setBusy] = useState(false);
  const [, setModal] = useGlobalState('ui.addNewPractitioner.modal');

  const [inputState, setInputState] = useState<InputStateT>({
    email: '',
    firstName: '',
    lastName: '',
    valid: false,
  });

  const navigate = useNavigate();

  const gs = useGlobalStateObject();

  return (
    <ThemeProvider themes={{ Input: inputTheme }}>
      <AdminPage
        contentClassName={S.container}
        subTitle="Practitioners / Add New Practitioner"
        title="Add New Practitioner"
      >
        <div styleName="content">
          <h3>Practitioner Information</h3>
          <NamesAndEmailsInputBlock
            setState={setInputState}
            state={inputState}
          />
          <div styleName="infoBlock">
            <IconInfo styleName="icon" />
            <p>
              Practitioner will receive an invitation in order to complete their
              profile and login credentials.
            </p>
          </div>
          <div styleName="buttons">
            <Button
              theme={secondaryButtonTheme}
              to={P_ADMIN_PRACTITIONERS}
            >
              Cancel and Return
            </Button>
            <Button
              disabled={!inputState.valid}
              onClick={() => {
                void (async () => {
                  try {
                    setBusy(true);
                    const res = await createArtist(inputState);

                    // Here we add the newly created artist record to the global
                    // state, the most bruteforce way:
                    const list = gs.get('artists.list.data');
                    if (list && (list.lastPage === 0 || !list.singlePage)) {
                      let items = [res.artist, ...list.items];
                      if (!list.singlePage && items.length > list.pageSize) {
                        items = items.slice(0, list.pageSize);
                      }
                      gs.set('artists.list.data', { ...list, items });
                    }

                    // And here we should redirect to the listing,
                    // and show the modal there.
                    setModal(res.newUser ? 'CREATED_NEW' : 'PROMOTED_EXISTING');
                  } catch (e) {
                    if (e && typeof e === 'object' && 'response' in e
                      && e.response && typeof e.response === 'object'
                      && 'data' in e.response && e.response.data
                      && typeof e.response.data === 'object'
                      && 'error' in e.response.data
                      && e.response.data.error === 1001
                    ) setModal('ALREADY_PRACTITIONER');
                    else setModal('ERROR');
                  }
                  setBusy(false);
                  void navigate(P_ADMIN_PRACTITIONERS);
                })();
              }}
            >
              Create Practitioner
            </Button>
          </div>
        </div>
        {busy ? <Throbber theme={throbberTheme} withOverlay /> : null}
      </AdminPage>
    </ThemeProvider>
  );
};

export default AdminAddNewPractitioner;
